import { useEffect, useState, type FormEvent } from 'react';
import { Package } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useInventory, useRecordMovement } from '@/hooks/useInventory';
import { LOCATION_LABELS, type Location } from '@/types/inventory';
import type { WorkTicket } from '@/types/production';

type Props = {
  open: boolean;
  onClose: () => void;
  ticket: WorkTicket;
};

export function ConsumeMaterialModal({ open, onClose, ticket }: Props) {
  const { data: items } = useInventory({ limit: 1000 });
  const log = useRecordMovement();

  const [itemId, setItemId] = useState('');
  const [quantity, setQuantity] = useState<number>(0);
  const [location, setLocation] = useState<Location>('panel_line');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setItemId('');
      setQuantity(0);
      setError(null);
    }
  }, [open]);

  const item = items?.find((i) => i.id === itemId);
  const availableLocations = item
    ? (Object.entries(item.onHand ?? {}) as Array<[Location, number]>)
        .filter(([, q]) => q > 0)
        .map(([loc, q]) => ({ loc, q }))
    : [];

  useEffect(() => {
    if (availableLocations.length > 0 && !availableLocations.find((a) => a.loc === location)) {
      setLocation(availableLocations[0].loc);
    }
  }, [itemId, availableLocations, location]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!itemId) { setError('Select a material.'); return; }
    if (quantity <= 0) { setError('Quantity must be greater than zero.'); return; }

    try {
      await log.mutateAsync({ itemId, type: 'consumption', quantity, location, orderId: ticket.id });
      toast.success(`Consumed ${quantity} ${item?.unit ?? ''} of ${item?.sku}`);
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Consumption failed.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker="Consume Material"
      title={`Log usage on ${ticket.number}`}
      description="Deducts stock from inventory and attributes the cost to this ticket."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={log.isPending}>Cancel</Button>
          <Button type="submit" form="cons-form" loading={log.isPending} leftIcon={<Package size={13} />}>
            Record Consumption
          </Button>
        </>
      }
    >
      <form id="cons-form" onSubmit={onSubmit} className="space-y-4 py-2">
        <Select
          label="Material"
          required
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          options={[
            { value: '', label: '— Select from inventory —' },
            ...(items ?? [])
              .filter((i) => i.active && (i.totalOnHand ?? 0) > 0)
              .map((i) => ({
                value: i.id,
                label: `${i.sku} · ${i.name} (${i.totalOnHand ?? 0} ${i.unit})`,
              })),
          ]}
        />

        {item && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={`Quantity (${item.unit})`}
                type="number" min="0" step="any"
                required
                value={quantity || ''}
                onChange={(e) => setQuantity(Number(e.target.value) || 0)}
              />
              <Select
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value as Location)}
                options={
                  availableLocations.length === 0
                    ? [{ value: '', label: 'No stock available' }]
                    : availableLocations.map(({ loc, q }) => ({
                        value: loc,
                        label: `${LOCATION_LABELS[loc]} (${q} ${item.unit})`,
                      }))
                }
              />
            </div>

            {quantity > 0 && item.unitCost !== undefined && (
              <div className="text-xs text-text-muted bg-bg-subtle border border-border rounded-sm p-3">
                <span className="kicker">Estimated Cost</span>
                <span className="ml-3 tabular text-text">
                  ${(quantity * item.unitCost).toFixed(2)}
                </span>
              </div>
            )}
          </>
        )}

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}
